import { Badge, type ColorDeBadge } from '@/shared/components/Badge'
import { formatDateTimeMX } from '@/shared/lib/dateUtils'
import {
  horaDeLaFirma,
  sincronizoDespues,
  etiquetaDeTerminos,
} from '@/features/almacen/lib/selloDeAceptacion'

/**
 * El sello de la aceptación de una asignación (A-02, CR-03).
 *
 * Es el registro legal de que alguien recibió la herramienta: quién, cuándo,
 * desde qué red y qué texto tenía enfrente. El back lo guardaba desde que
 * existe el flujo pero no lo devolvía, así que en la web la asignación se veía
 * como si nunca se hubiera aceptado.
 *
 * No es un movimiento aparte, y por eso vive dentro del detalle de la
 * asignación: la herramienta no cambia de manos al aceptarla, ya estaba con esa
 * persona desde que se la asignaron. Lo que cambia es que ahora consta.
 *
 * La hora del servidor es la que vale. La del dispositivo solo se muestra
 * cuando difiere: en obra se acepta sin conexión y puede sincronizar horas
 * después, y verlas juntas explica una diferencia que si no parece un error.
 */

export interface DatosDelSello {
  approvalStatus?: 'pending' | 'approved' | 'rejected' | null
  approvalResolvedAt?: string | null
  approvalDeviceTimestamp?: string | null
  approvalIp?: string | null
  termsVersion?: string | null
  termsVersionTitle?: string | null
  signatureUrl?: string | null
  rejectionReason?: string | null
}

const LABEL = 'text-xs text-gray-500 mb-1'
const VALOR = 'text-sm text-gray-800'

const ESTADO: Record<string, { texto: string; color: ColorDeBadge; quien: string }> = {
  pending: {
    texto: 'Pendiente de aceptación',
    color: 'amarillo',
    quien: 'Debe aceptar',
  },
  approved: { texto: 'Aceptada', color: 'verde', quien: 'Aceptada por' },
  rejected: { texto: 'Rechazada', color: 'rojo', quien: 'Rechazada por' },
}

export const SelloDeAceptacion = ({
  sello,
  receptor,
  className = '',
}: {
  sello: DatosDelSello
  /** Quien recibe: es el único que puede resolver, así que es quien firma el sello. */
  receptor: string | null
  className?: string
}) => {
  const estado = sello.approvalStatus ? ESTADO[sello.approvalStatus] : null

  // Una asignación anterior al flujo de aprobación no tiene nada que sellar, y
  // una tarjeta vacía se lee como un dato que falta.
  if (!estado) return null

  const resuelta = sello.approvalStatus !== 'pending'
  const firmadoEn = horaDeLaFirma(
    sello.approvalDeviceTimestamp,
    sello.approvalResolvedAt,
  )
  const sincronizoAparte = sincronizoDespues(
    sello.approvalDeviceTimestamp,
    sello.approvalResolvedAt,
  )
  const terminos = etiquetaDeTerminos(sello.termsVersion, sello.termsVersionTitle)

  return (
    <div
      className={`bg-white rounded-xl border shadow-sm ${className}`}
      style={{ borderColor: '#E0E0E0' }}
    >
      <div
        className="px-6 py-4 flex items-center justify-between gap-4"
        style={{ borderColor: '#E0E0E0', borderBottom: '1px solid #E0E0E0' }}
      >
        <h2 className="text-base font-semibold text-gray-800">Sello de aceptación</h2>
        <Badge color={estado.color} className="flex-shrink-0">
          {estado.texto}
        </Badge>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-4 gap-5">
          <div>
            <p className={LABEL}>{estado.quien}</p>
            <p className={VALOR}>{receptor ?? 'Sin responsable'}</p>
          </div>

          {/*
            La hora que se certifica es la de la firma, no la de llegada al
            servidor (S-02). Si la aceptación pasó por la cola de sincronización
            esas dos no son la misma, y el sello tiene un objetivo legal: debe
            decir cuándo la persona aceptó los términos.
          */}
          {resuelta && (
            <div>
              <p className={LABEL}>Fecha y hora de la firma</p>
              <p className={VALOR}>
                {firmadoEn ? formatDateTimeMX(firmadoEn) : 'Sin registro'}
              </p>
            </div>
          )}

          {resuelta && (
            <div>
              <p className={LABEL}>Dirección IP</p>
              <p className={VALOR}>{sello.approvalIp ?? 'Sin registro'}</p>
            </div>
          )}

          {resuelta && (
            <div>
              <p className={LABEL}>Términos aceptados</p>
              <p className={VALOR}>{terminos ?? 'Sin registro'}</p>
            </div>
          )}

          {/*
            Solo cuando hubo un hueco real: si sincronizó en segundos, repetir
            casi la misma hora no explica nada y estorba.
          */}
          {sincronizoAparte && (
            <div>
              <p className={LABEL}>Sincronizado</p>
              <p className={VALOR}>
                {formatDateTimeMX(sello.approvalResolvedAt)}
              </p>
            </div>
          )}
        </div>

        {sello.rejectionReason?.trim() && (
          <div className="mt-5 pt-5 border-t" style={{ borderColor: '#E0E0E0' }}>
            <p className={LABEL}>Motivo de rechazo</p>
            <p className="text-sm text-gray-800 whitespace-pre-line">
              {sello.rejectionReason.trim()}
            </p>
          </div>
        )}

        {/* Rechazar no pide firma: pide motivo. */}
        {sello.signatureUrl && (
          <div className="mt-5 pt-5 border-t" style={{ borderColor: '#E0E0E0' }}>
            <p className={LABEL}>Firma de conformidad</p>
            <img
              src={sello.signatureUrl}
              alt="Firma de quien recibió la herramienta"
              className="h-28 object-contain object-left"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default SelloDeAceptacion
